/**
 * 抓包重写规则 Tab（P2-1 重写引擎）
 * 规则表格（启停/排序/类型 Tag）+ 表单抽屉（按 rule_type 动态渲染动作行）
 * 规则保存后热更新代理内存缓存，转发路径即时生效（与录制开关解耦）
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Button, Drawer, Empty, Input, InputNumber, message, Popconfirm,
  Select, Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  createRewriteRule, deleteRewriteRule, getRewriteRules,
  sortRewriteRules, updateRewriteRule,
} from '@/api/capture'
import type { CaptureRewriteAction, CaptureRewriteRule } from '@/types'

const { TextArea } = Input
const { Text } = Typography

const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace"

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']

/** 规则类型元信息 */
const RULE_TYPE_META: Record<CaptureRewriteRule['rule_type'], { label: string; color: string }> = {
  redirect: { label: '重定向', color: 'gold' },
  requestReplace: { label: '请求替换', color: 'blue' },
  responseReplace: { label: '响应替换', color: 'purple' },
  requestUpdate: { label: '请求增删改', color: 'cyan' },
  block: { label: '屏蔽', color: 'red' },
}

/** 动作类型选项（按 rule_type 区分） */
const ACTION_TYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  requestReplace: [
    { value: 'requestLine', label: '替换请求行' },
    { value: 'requestHeader', label: '替换请求头' },
    { value: 'requestBody', label: '替换请求体' },
  ],
  responseReplace: [
    { value: 'responseStatus', label: '替换状态码' },
    { value: 'responseHeader', label: '替换响应头' },
    { value: 'responseBody', label: '替换响应体' },
  ],
  requestUpdate: [
    { value: 'query', label: 'Query 参数' },
    { value: 'header', label: '请求头' },
    { value: 'body', label: '请求体（替换）' },
  ],
}

const ACTION_LABEL: Record<string, string> = Object.fromEntries(
  Object.values(ACTION_TYPE_OPTIONS).flatMap((opts) => opts.map((o) => [o.value, o.label])),
)

/** 动作明细摘要（表格列展示） */
const actionSummary = (rule: CaptureRewriteRule): string => {
  if (rule.rule_type === 'redirect') return rule.rewrite_url || '-'
  if (rule.rule_type === 'block') return `${rule.block_status}${rule.block_body ? ' · 自定义返回体' : ''}`
  if (!rule.actions?.length) return '-'
  return rule.actions
    .map((a) => {
      const v = a.values || {}
      if (a.type === 'requestLine') return `请求行→${v.method || ''} ${v.path || ''}`.trim()
      if (a.type === 'responseStatus') return `状态码→${v.statusCode || ''}`
      if (a.type === 'query') return `Query ${v.key || ''}(${v.op || 'update'})`
      if (a.type === 'header' || a.type === 'requestHeader' || a.type === 'responseHeader') return `头 ${v.key || ''}`
      if (a.type === 'body' || a.type === 'requestBody' || a.type === 'responseBody') return '报文体'
      return ACTION_LABEL[a.type] || a.type
    })
    .join('；')
}

/** 空白规则模板 */
const emptyRule = (sortOrder: number): CaptureRewriteRule => ({
  id: 0,
  name: '',
  enabled: true,
  method: '',
  url_pattern: '',
  use_regex: false,
  rule_type: 'requestReplace',
  actions: [],
  rewrite_url: '',
  block_status: 403,
  block_body: '',
  sort_order: sortOrder,
  remark: '',
  create_time: '',
  update_time: '',
})

/** 按动作类型渲染参数字段 */
function ActionFields({ action, onChange }: { action: CaptureRewriteAction; onChange: (next: CaptureRewriteAction) => void }) {
  const v = action.values || {}
  const set = (patch: Record<string, string>) => onChange({ ...action, values: { ...v, ...patch } })
  const inputStyle = { borderRadius: 999 } as const

  switch (action.type) {
    case 'requestLine':
      return (
        <Space wrap size={8}>
          <Input placeholder="方法（GET/POST…，可空）" value={v.method} onChange={(e) => set({ method: e.target.value })} style={{ ...inputStyle, width: 170 }} />
          <Input placeholder="新路径（可含 query）" value={v.path} onChange={(e) => set({ path: e.target.value })} style={{ ...inputStyle, width: 320 }} />
        </Space>
      )
    case 'requestHeader':
    case 'responseHeader':
      return (
        <Space wrap size={8}>
          <Input placeholder="Header 名" value={v.key} onChange={(e) => set({ key: e.target.value })} style={{ ...inputStyle, width: 180 }} />
          <Input placeholder="Header 值" value={v.value} onChange={(e) => set({ value: e.target.value })} style={{ ...inputStyle, width: 310 }} />
        </Space>
      )
    case 'requestBody':
    case 'responseBody':
    case 'body':
      return <TextArea placeholder="替换后的报文体" value={v.body} onChange={(e) => set({ body: e.target.value })} rows={3} style={{ borderRadius: 8 }} />
    case 'responseStatus':
      return (
        <InputNumber
          placeholder="状态码"
          min={100}
          max={599}
          value={v.statusCode ? Number(v.statusCode) : undefined}
          onChange={(val) => set({ statusCode: val != null ? String(val) : '' })}
          style={{ width: 180, borderRadius: 999 }}
        />
      )
    case 'query':
    case 'header':
      return (
        <Space wrap size={8}>
          <Select
            value={v.op || 'update'}
            onChange={(op) => set({ op })}
            style={{ width: 100 }}
            options={[
              { value: 'add', label: '新增' },
              { value: 'update', label: '修改' },
              { value: 'remove', label: '删除' },
            ]}
          />
          <Input placeholder={action.type === 'query' ? '参数名' : 'Header 名'} value={v.key} onChange={(e) => set({ key: e.target.value })} style={{ ...inputStyle, width: 170 }} />
          {(v.op || 'update') !== 'remove' && (
            <Input placeholder={action.type === 'query' ? '参数值' : 'Header 值'} value={v.value} onChange={(e) => set({ value: e.target.value })} style={{ ...inputStyle, width: 210 }} />
          )}
        </Space>
      )
    default:
      return null
  }
}

export default function RewriteRulesTab({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [rules, setRules] = useState<CaptureRewriteRule[]>([])
  const [loading, setLoading] = useState(false)
  // 表单抽屉
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<CaptureRewriteRule | null>(null)
  const [saving, setSaving] = useState(false)

  const loadRules = useCallback(async () => {
    setLoading(true)
    try {
      const list = await getRewriteRules()
      setRules(list)
      onCountChange?.(list.length)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadRules()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** 打开新建/编辑抽屉 */
  const openEditor = (rule?: CaptureRewriteRule) => {
    if (rule) {
      setEditing({ ...rule, actions: (rule.actions || []).map((a) => ({ type: a.type, values: { ...(a.values || {}) } })) })
    } else {
      const maxSort = rules.reduce((max, r) => Math.max(max, r.sort_order), 0)
      setEditing(emptyRule(maxSort + 1))
    }
    setEditorOpen(true)
  }

  /** 保存（新建或更新） */
  const handleSave = async () => {
    if (!editing) return
    if (!editing.url_pattern.trim()) {
      message.warning('请填写 URL 匹配模式')
      return
    }
    if (editing.rule_type === 'redirect' && !editing.rewrite_url.trim().startsWith('http')) {
      message.warning('重定向目标地址必须以 http:// 或 https:// 开头')
      return
    }
    const needActions = ['requestReplace', 'responseReplace', 'requestUpdate'].includes(editing.rule_type)
    if (needActions && editing.actions.length === 0) {
      message.warning('请至少添加一条动作明细')
      return
    }
    setSaving(true)
    try {
      const payload: Partial<CaptureRewriteRule> = {
        name: editing.name,
        enabled: editing.enabled,
        method: editing.method,
        url_pattern: editing.url_pattern,
        use_regex: editing.use_regex,
        rule_type: editing.rule_type,
        actions: editing.actions,
        rewrite_url: editing.rewrite_url,
        block_status: editing.block_status,
        block_body: editing.block_body,
        sort_order: editing.sort_order,
        remark: editing.remark,
      }
      if (editing.id) {
        await updateRewriteRule(editing.id, payload)
        message.success('规则已更新并生效')
      } else {
        await createRewriteRule(payload)
        message.success('规则已创建并生效')
      }
      setEditorOpen(false)
      loadRules()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setSaving(false)
    }
  }

  /** 启停切换 */
  const handleToggle = async (rule: CaptureRewriteRule, enabled: boolean) => {
    try {
      await updateRewriteRule(rule.id, { enabled })
      message.success(enabled ? '规则已启用' : '规则已停用')
      loadRules()
    } catch {
      /* 拦截器已提示 */
    }
  }

  /** 上移/下移（交换 sort_order） */
  const handleMove = async (index: number, dir: -1 | 1) => {
    const target = rules[index + dir]
    const current = rules[index]
    if (!target) return
    try {
      await sortRewriteRules([
        { id: current.id, sort_order: target.sort_order },
        { id: target.id, sort_order: current.sort_order },
      ])
      loadRules()
    } catch {
      /* 拦截器已提示 */
    }
  }

  /** 删除 */
  const handleDelete = async (rule: CaptureRewriteRule) => {
    await deleteRewriteRule(rule.id)
    message.success('规则已删除')
    loadRules()
  }

  /** 编辑动作数组 */
  const updateAction = (idx: number, next: CaptureRewriteAction) => {
    if (!editing) return
    setEditing({ ...editing, actions: editing.actions.map((a, i) => (i === idx ? next : a)) })
  }

  const columns: ColumnsType<CaptureRewriteRule> = [
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 76,
      align: 'center',
      render: (v: boolean, r) => <Switch size="small" checked={v} onChange={(val) => handleToggle(r, val)} />,
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 160,
      ellipsis: true,
      render: (v: string, r) => (
        <Tooltip title={r.remark || v}>
          <Text strong={v !== '未命名规则'} style={{ fontSize: 13 }}>{v || '未命名规则'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '类型',
      dataIndex: 'rule_type',
      width: 110,
      render: (v: CaptureRewriteRule['rule_type']) => {
        const meta = RULE_TYPE_META[v]
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{v}</Tag>
      },
    },
    {
      title: '匹配',
      key: 'match',
      ellipsis: true,
      render: (_, r) => (
        <span style={{ fontFamily: mono, fontSize: 12 }}>
          {r.method && <Tag color="blue" style={{ marginRight: 6 }}>{r.method}</Tag>}
          {r.url_pattern}
          {r.use_regex && <Tag style={{ marginLeft: 6 }} color="geekblue">正则</Tag>}
        </span>
      ),
    },
    {
      title: '明细',
      key: 'detail',
      ellipsis: true,
      render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{actionSummary(r)}</Text>,
    },
    {
      title: '顺序',
      dataIndex: 'sort_order',
      width: 110,
      align: 'center',
      render: (v: number, _, index: number) => (
        <Space size={0}>
          <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>{v}</Text>
          <Button type="link" size="small" disabled={index === 0} onClick={() => handleMove(index, -1)}>上移</Button>
          <Button type="link" size="small" disabled={index === rules.length - 1} onClick={() => handleMove(index, 1)}>下移</Button>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => openEditor(r)}>编辑</Button>
          <Popconfirm title="确定删除该规则？" onConfirm={() => handleDelete(r)}>
            <Button type="link" size="small" danger className="danger">删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const isUpdateType = editing?.rule_type === 'requestUpdate'
  const actionOptions = editing ? ACTION_TYPE_OPTIONS[editing.rule_type] || [] : []

  return (
    <>
      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap', padding: '0 22px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>规则保存后即时生效（作用于转发路径，与录制开关无关）</Text>
        <div style={{ flex: 1 }} />
        <Button onClick={loadRules}>刷新</Button>
        <Button type="primary" style={{ borderRadius: 999, fontWeight: 700 }} onClick={() => openEditor()}>
          新建规则
        </Button>
      </div>
      <Table<CaptureRewriteRule>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rules}
        scroll={{ y: '100%' }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无重写规则，点击「新建规则」创建第一条规则" /> }}
        pagination={false}
      />

      {/* 规则表单抽屉 */}
      <Drawer
        title={editing?.id ? '编辑重写规则' : '新建重写规则'}
        width={640}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setEditorOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={handleSave} style={{ borderRadius: 999, fontWeight: 700 }}>保存</Button>
          </div>
        }
      >
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text strong style={{ width: 88 }}>规则名称</Text>
              <Input placeholder="未命名规则" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} style={{ borderRadius: 999, flex: 1 }} />
              <Space size={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>启用</Text>
                <Switch checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} />
              </Space>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text strong style={{ width: 88 }}>规则类型</Text>
              <Select
                value={editing.rule_type}
                onChange={(v) => setEditing({ ...editing, rule_type: v, actions: [] })}
                style={{ flex: 1 }}
                options={Object.entries(RULE_TYPE_META).map(([value, meta]) => ({ value, label: meta.label }))}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Text strong style={{ width: 88, lineHeight: '36px' }}>URL 匹配</Text>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                  placeholder={editing.use_regex ? '正则表达式，如 .*\\.example\\.com/api' : '字面量包含匹配，如 api.example.com'}
                  value={editing.url_pattern}
                  onChange={(e) => setEditing({ ...editing, url_pattern: e.target.value })}
                  style={{ borderRadius: 999, fontFamily: mono }}
                />
                <Space size={12}>
                  <Select
                    value={editing.method || undefined}
                    allowClear
                    placeholder="任意方法"
                    onChange={(v) => setEditing({ ...editing, method: v || '' })}
                    style={{ width: 130 }}
                    options={METHOD_OPTIONS.map((m) => ({ value: m, label: m }))}
                  />
                  <Space size={6}>
                    <Text type="secondary" style={{ fontSize: 12 }}>正则匹配</Text>
                    <Switch size="small" checked={editing.use_regex} onChange={(v) => setEditing({ ...editing, use_regex: v })} />
                  </Space>
                </Space>
              </div>
            </div>

            {editing.rule_type === 'redirect' && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Text strong style={{ width: 88 }}>重定向到</Text>
                <Input
                  placeholder="https://target.example.com/path（完整 URL）"
                  value={editing.rewrite_url}
                  onChange={(e) => setEditing({ ...editing, rewrite_url: e.target.value })}
                  style={{ borderRadius: 999, fontFamily: mono, flex: 1 }}
                />
              </div>
            )}

            {editing.rule_type === 'block' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Text strong style={{ width: 88 }}>返回状态码</Text>
                  <InputNumber
                    min={100}
                    max={599}
                    value={editing.block_status}
                    onChange={(v) => setEditing({ ...editing, block_status: v ?? 403 })}
                    style={{ width: 140, borderRadius: 999 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <Text strong style={{ width: 88, marginTop: 5 }}>返回体</Text>
                  <TextArea
                    placeholder="留空返回默认提示文本"
                    value={editing.block_body}
                    onChange={(e) => setEditing({ ...editing, block_body: e.target.value })}
                    rows={3}
                    style={{ flex: 1, borderRadius: 8 }}
                  />
                </div>
              </div>
            )}

            {(editing.rule_type === 'requestReplace' || editing.rule_type === 'responseReplace' || editing.rule_type === 'requestUpdate') && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Text strong style={{ width: 88, marginTop: 5 }}>动作明细</Text>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {editing.actions.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>尚未添加动作，点击下方按钮新增</Text>
                  )}
                  {editing.actions.map((action, idx) => (
                    <div key={idx} style={{ background: 'var(--au-primary-50, #f6f8f5)', border: '1px solid var(--au-border, #e4e2dc)', borderRadius: 8, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Select
                          value={action.type}
                          onChange={(t) => updateAction(idx, { type: t, values: {} })}
                          style={{ width: 170 }}
                          options={actionOptions}
                        />
                        <Button type="link" size="small" danger className="danger" onClick={() => setEditing({ ...editing, actions: editing.actions.filter((_, i) => i !== idx) })}>
                          移除
                        </Button>
                      </div>
                      <ActionFields action={action} onChange={(next) => updateAction(idx, next)} />
                    </div>
                  ))}
                  <Button
                    style={{ borderRadius: 999, width: 'fit-content' }}
                    onClick={() => setEditing({ ...editing, actions: [...editing.actions, { type: actionOptions[0]?.value || '', values: {} }] })}
                  >
                    添加动作
                  </Button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text strong style={{ width: 88 }}>执行顺序</Text>
              <InputNumber
                min={0}
                value={editing.sort_order}
                onChange={(v) => setEditing({ ...editing, sort_order: v ?? 0 })}
                style={{ width: 140, borderRadius: 999 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>{isUpdateType ? '请求增删改：同一匹配可配置多条动作' : '数字越小越先应用'}</Text>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text strong style={{ width: 88 }}>备注</Text>
              <Input placeholder="可选" value={editing.remark} onChange={(e) => setEditing({ ...editing, remark: e.target.value })} style={{ borderRadius: 999, flex: 1 }} />
            </div>
          </div>
        )}
      </Drawer>
    </>
  )
}
